// components/copilot/MessageBubble.jsx
import React, { useEffect, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

import { fmtMoney, fmtTime } from "./utils/format";
import { copyToClipboard } from "./utils/clipboard";
import { isImageAttachment } from "./utils/attachments";

function CopyableTable({ children, ...props }) {
  const [copied, setCopied] = useState(false);

  const extractText = (node) => {
    if (!node) return "";
    if (typeof node === "string") return node;
    if (Array.isArray(node)) return node.map(extractText).join("");
    if (node?.props?.children !== undefined) return extractText(node.props.children);
    return "";
  };

  const onCopyTable = async () => {
    // Collect rows as arrays of cell strings
    const rows = [];
    const collectRows = (n) => {
      if (!n) return;
      if (Array.isArray(n)) { n.forEach(collectRows); return; }
      if (n?.type === "tr") {
        const cells = [];
        const collectCells = (c) => {
          if (!c) return;
          if (Array.isArray(c)) { c.forEach(collectCells); return; }
          if (c?.type === "td" || c?.type === "th") {
            cells.push(extractText(c?.props?.children).replace(/\t/g, " ").trim());
          } else if (c?.props?.children) {
            collectCells(c.props.children);
          }
        };
        collectCells(n.props?.children);
        if (cells.length) rows.push(cells);
        return;
      }
      if (n?.props?.children) collectRows(n.props.children);
    };
    collectRows(children);

    if (!rows.length) return;

    // Build HTML table — pastes as real table in Notion, Google Docs, Excel, Word
    const escHtml = (s) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
    const htmlRows = rows.map((cells, ri) => {
      const tag = ri === 0 ? "th" : "td";
      return "<tr>" + cells.map((c) => `<${tag}>${escHtml(c)}</${tag}>`).join("") + "</tr>";
    }).join("");
    const html = `<table>${htmlRows}</table>`;

    // TSV fallback for plain-text targets
    const tsv = rows.map((r) => r.join("\t")).join("\n");

    try {
      if (window.ClipboardItem && navigator.clipboard?.write) {
        await navigator.clipboard.write([
          new ClipboardItem({
            "text/html": new Blob([html], { type: "text/html" }),
            "text/plain": new Blob([tsv], { type: "text/plain" }),
          }),
        ]);
      } else {
        await navigator.clipboard.writeText(tsv);
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // last resort
      try { await navigator.clipboard.writeText(tsv); setCopied(true); setTimeout(() => setCopied(false), 1500); } catch {}
    }
  };

  return (
    <div className="copilot-table-wrap" style={{ position: "relative" }}>
      <button
        type="button"
        onClick={onCopyTable}
        aria-label="Copy table"
        title="Copy table"
        style={{
          position: "absolute",
          top: 8,
          right: 8,
          width: 28,
          height: 28,
          padding: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          borderRadius: 6,
          border: copied ? "1px solid #6ee7b7" : "1px solid rgba(0,0,0,.13)",
          background: copied ? "#d1fae5" : "rgba(255,255,255,0.92)",
          cursor: "pointer",
          zIndex: 1,
          boxShadow: "0 1px 3px rgba(0,0,0,.08)",
          transition: "background 0.15s, border-color 0.15s, opacity 0.15s",
          opacity: copied ? 1 : 0.7,
        }}
        onMouseEnter={(e) => { if (!copied) { e.currentTarget.style.opacity = "1"; e.currentTarget.style.background = "#fff"; }}}
        onMouseLeave={(e) => { if (!copied) { e.currentTarget.style.opacity = "0.7"; e.currentTarget.style.background = "rgba(255,255,255,0.92)"; }}}
        onMouseDown={(e) => e.preventDefault()}
      >
        {copied ? (
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M5 13l4 4L19 7" stroke="#059669" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        ) : (
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M9 9h10v10H9V9Z" stroke="#374151" strokeWidth="2" strokeLinejoin="round"/>
            <path d="M5 15H4a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1h10a1 1 0 0 1 1 1v1" stroke="#374151" strokeWidth="2" strokeLinecap="round"/>
          </svg>
        )}
      </button>
      <table {...props}>{children}</table>
    </div>
  );
}

function QualityBadge({ text }) {
  // Parse quality info from the Data Quality Report section in the message text
  if (!text) return null;
  const marker = "## Data Quality Report";
  const idx = text.indexOf(marker);
  if (idx < 0) return null;

  const section = text.slice(idx);
  const errorMatch = section.match(/\*\*Errors \((\d+)\)/);
  const warningMatch = section.match(/\*\*Warnings \((\d+)\)/);
  const correctedMatch = section.match(/\*\*Auto-corrected \((\d+)\)/);
  const hasCorrection = section.includes("## Auto-Correction (data accuracy fix)");

  const errors = errorMatch ? parseInt(errorMatch[1], 10) : 0;
  const warnings = warningMatch ? parseInt(warningMatch[1], 10) : 0;
  const corrected = correctedMatch ? parseInt(correctedMatch[1], 10) : 0;

  if (errors === 0 && warnings === 0 && corrected === 0 && !hasCorrection) return null;

  let color, bg, border, label;
  if (errors > 0) {
    color = "#991b1b"; bg = "#fef2f2"; border = "#fecaca";
    label = `${errors} error${errors > 1 ? "s" : ""}`;
    if (hasCorrection) label += " (corrected)";
  } else if (warnings > 0) {
    color = "#92400e"; bg = "#fffbeb"; border = "#fde68a";
    label = `${warnings} warning${warnings > 1 ? "s" : ""}`;
  } else {
    color = "#065f46"; bg = "#ecfdf5"; border = "#a7f3d0";
    label = `${corrected} auto-fixed`;
  }

  return (
    <span
      title="Data Quality Report — scroll down for details"
      style={{
        display: "inline-flex", alignItems: "center", gap: 4,
        fontSize: 11, fontWeight: 600,
        color, background: bg, border: `1px solid ${border}`,
        borderRadius: 6, padding: "2px 7px",
        cursor: "default", whiteSpace: "nowrap",
      }}
    >
      <span style={{ width: 6, height: 6, borderRadius: "50%", background: color, flexShrink: 0 }} />
      {label}
    </span>
  );
}

export default function MessageBubble({
  msg,
  getAttachmentObjectUrl,
  onOpenImage,
  onBranchFromMessage,
  disableBranch,
  disableCopy,
  onExportPpt,
  disableExport,
  forceThinking = false,
}) {
  const role = msg?.role || "assistant";
  const isUser = role === "user";
  const isAssistant = role === "assistant";
  const isErr = (msg?.status || "") === "error" || !!(msg?.error_text || "").trim();

  const msgInProgress =
    forceThinking ||
    String(msg?.status || "").toLowerCase() === "running" ||
    String(msg?.status || "").toLowerCase() === "streaming" ||
    String(msg?.status || "").toLowerCase() === "pending";

  const text = (msg?.text || "").trim();
  const errorText = (msg?.error_text || "").trim();

  const attachments = Array.isArray(msg?.attachments) ? msg.attachments : [];
  const imageAttachments = attachments.filter(isImageAttachment);

  const [thumbUrls, setThumbUrls] = useState(() => ({})); // id -> objectURL
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      const next = {};
      for (const a of imageAttachments) {
        const id = a?.id;
        if (!id) continue;
        try {
          const u = await getAttachmentObjectUrl(id);
          next[id] = u;
        } catch {
          // ignore per attachment
        }
      }
      if (!cancelled) setThumbUrls((prev) => ({ ...prev, ...next }));
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imageAttachments.map((x) => x?.id).join(","), getAttachmentObjectUrl]);

  const copyPayload = isErr ? `Error: ${errorText || "Unknown error"}${text ? `\n\n${text}` : ""}` : text;
  const canCopy = !disableCopy && !!copyPayload && !msgInProgress;
  const canExport = !disableExport && !msgInProgress && (!!copyPayload || imageAttachments.length > 0);

  const onCopy = async () => {
    if (!canCopy) return;
    const ok = await copyToClipboard(copyPayload);
    if (!ok) return;
    setCopied(true);
    setTimeout(() => setCopied(false), 900);
  };

  const iconBtnStyle = (disabled) => ({
    border: "1px solid rgba(0,0,0,.15)",
    background: disabled ? "#f3f4f6" : "#ffffff",
    padding: "6px 8px",
    borderRadius: 10,
    cursor: disabled ? "not-allowed" : "pointer",
    opacity: disabled ? 0.55 : 1,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    boxShadow: "0 1px 2px rgba(0,0,0,.06)",
    color: "#111827",
  });

  const onIconEnter = (e, disabled) => {
    if (disabled) return;
    e.currentTarget.style.background = "#f9fafb";
    e.currentTarget.style.borderColor = "rgba(0,0,0,.22)";
  };
  const onIconLeave = (e, disabled) => {
    if (disabled) return;
    e.currentTarget.style.background = "#ffffff";
    e.currentTarget.style.borderColor = "rgba(0,0,0,.15)";
  };

  return (
    <div style={{ display: "flex", justifyContent: isUser ? "flex-end" : "flex-start", marginBottom: 10 }}>
      <div style={{ display: "flex", flexDirection: "column", alignItems: isUser ? "flex-end" : "flex-start" }}>
        <div className={["copilot-bubble", isUser ? "is-user" : "is-assistant", isErr ? "is-error" : ""].join(" ")}>
          <div className="copilot-bubble-meta" style={{ display: "flex", justifyContent: "space-between", gap: 10 }}>
            <div>
              <strong style={{ textTransform: "uppercase" }}>{role}</strong>
              {msg?.created_at ? <span> • {fmtTime(msg.created_at)}</span> : null}
              {msg?.model ? <span> • {msg.model}</span> : null}
              {typeof msg?.cost_usd === "number" ? <span> • {fmtMoney(msg.cost_usd)}</span> : null}
              {isAssistant && !isErr && !msgInProgress ? <QualityBadge text={text} /> : null}
            </div>
          </div>

          {imageAttachments.length > 0 ? (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 8, marginBottom: 8 }}>
              {imageAttachments.map((a) => {
                const id = a?.id;
                const u = id ? thumbUrls[id] : null;
                const title = a?.filename || `Image #${id}`;
                return (
                  <div
                    key={id}
                    style={{
                      width: 120,
                      borderRadius: 12,
                      overflow: "hidden",
                      border: "1px solid rgba(0,0,0,.15)",
                      background: "#fff",
                      cursor: "pointer",
                    }}
                    title="Click to preview"
                    onClick={() => {
                      if (u) onOpenImage(u, title);
                    }}
                  >
                    {u ? (
                      <img src={u} alt={title} style={{ width: "100%", height: 90, objectFit: "cover", display: "block" }} />
                    ) : (
                      <div style={{ height: 90, display: "flex", alignItems: "center", justifyContent: "center", color: "#6b7280" }}>
                        Loading…
                      </div>
                    )}
                    <div style={{ padding: "6px 8px", fontSize: 11, color: "#374151" }}>
                      {title.length > 18 ? `${title.slice(0, 18)}…` : title}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : null}

          {isErr ? (
            <div className="copilot-bubble-error">
              {errorText || "Unknown error"}
              {text ? (
                <div className="copilot-bubble-error-extra">
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>{text}</ReactMarkdown>
                </div>
              ) : null}
            </div>
          ) : isUser ? (
            <div style={{ whiteSpace: "pre-wrap" }}>{text}</div>
          ) : msgInProgress ? (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                padding: "12px 16px",
                minHeight: 44,
                minWidth: 320, // ✅ longer bubble content area
                color: "#6b7280",
                fontWeight: 400, // ✅ less bold-y
                fontSize: 14,
                opacity: 0.9,
              }}
            >
              <span>MAD Pilot is thinking…</span>
              <span
                aria-hidden="true"
                style={{
                  width: 14,
                  height: 14,
                  borderRadius: 999,
                  border: "2px solid rgba(107,114,128,.35)",
                  borderTopColor: "rgba(107,114,128,1)",
                  animation: "copilotSpin 0.9s linear infinite",
                }}
              />
            </div>
          ) : (
            <div className="copilot-md">
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={{
                  a: ({ node, ...props }) => <a {...props} target="_blank" rel="noreferrer" />,
                  code: ({ inline, className, children, ...props }) => {
                    const val = String(children || "");
                    if (inline)
                      return (
                        <code className="copilot-code-inline" {...props}>
                          {val}
                        </code>
                      );
                    return (
                      <pre className="copilot-code-block">
                        <code className={className || ""} {...props}>
                          {val}
                        </code>
                      </pre>
                    );
                  },
                  table: ({ node, ...props }) => <CopyableTable {...props} />,
                }}
              >
                {text || "—"}
              </ReactMarkdown>
            </div>
          )}

          {msg?.usage?.total_tokens != null ? (
            <div style={{ fontSize: 11, color: isUser ? "#e5e7eb" : "#6b7280", marginTop: 8 }}>
              Tokens: {msg.usage.prompt_tokens ?? "—"} in / {msg.usage.completion_tokens ?? "—"} out • total {msg.usage.total_tokens}
            </div>
          ) : null}
        </div>

        <div style={{ display: "flex", gap: 8, marginTop: 8, justifyContent: isUser ? "flex-end" : "flex-start" }}>
          <button
            type="button"
            disabled={!canCopy}
            onClick={onCopy}
            title={copied ? "Copied!" : "Copy"}
            aria-label="Copy"
            style={iconBtnStyle(!canCopy)}
            onMouseDown={(e) => e.preventDefault()}
            onMouseEnter={(e) => onIconEnter(e, !canCopy)}
            onMouseLeave={(e) => onIconLeave(e, !canCopy)}
          >
            {copied ? (
              <span style={{ fontSize: 12, fontWeight: 700, lineHeight: "16px" }}>✓</span>
            ) : (
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ display: "block" }}>
                <path d="M9 9h10v10H9V9Z" stroke="#111827" strokeWidth="2" strokeLinejoin="round" />
                <path
                  d="M5 15H4a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1h10a1 1 0 0 1 1 1v1"
                  stroke="#111827"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
            )}
          </button>

          <button
            type="button"
            disabled={!canExport}
            onClick={() => onExportPpt?.()}
            title="Export to PPT"
            aria-label="Export to PPT"
            style={iconBtnStyle(!canExport)}
            onMouseDown={(e) => e.preventDefault()}
            onMouseEnter={(e) => onIconEnter(e, !canExport)}
            onMouseLeave={(e) => onIconLeave(e, !canExport)}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ display: "block" }}>
              <path
                d="M7 3h7l3 3v15a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1Z"
                stroke="#111827"
                strokeWidth="2"
                strokeLinejoin="round"
              />
              <path d="M14 3v4h4" stroke="#111827" strokeWidth="2" strokeLinejoin="round" />
              <path d="M8 12h8" stroke="#111827" strokeWidth="2" strokeLinecap="round" />
              <path d="M8 16h6" stroke="#111827" strokeWidth="2" strokeLinecap="round" />
            </svg>
          </button>

          {isAssistant ? (
            <button
              type="button"
              disabled={disableBranch || msgInProgress}
              onClick={() => onBranchFromMessage?.(msg.id)}
              title="Branch"
              aria-label="Branch"
              style={iconBtnStyle(disableBranch || msgInProgress)}
              onMouseDown={(e) => e.preventDefault()}
              onMouseEnter={(e) => onIconEnter(e, disableBranch || msgInProgress)}
              onMouseLeave={(e) => onIconLeave(e, disableBranch || msgInProgress)}
              onMouseUp={(e) => {
                if (disableBranch || msgInProgress) return;
                e.currentTarget.style.background = "#f9fafb";
              }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ display: "block" }}>
                <path
                  d="M7 3a2 2 0 1 0 0 4 2 2 0 0 0 0-4Zm0 6v6c0 2.21 1.79 4 4 4h2"
                  stroke="#111827"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M17 17a2 2 0 1 0 0 4 2 2 0 0 0 0-4Zm0-14a2 2 0 1 0 0 4 2 2 0 0 0 0-4Z"
                  stroke="#111827"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M17 7v6a4 4 0 0 1-4 4h-2"
                  stroke="#111827"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}